import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Github, Activity, Shield, Smartphone, Wifi, Database, Users, AlertTriangle, Clock, QrCode, Server } from 'lucide-react';
import './SoulProject.css';

function HealthIotProject() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        {
            icon: <Activity size={32} />,
            title: "Real-Time Monitoring",
            desc: "Live vital tracking including Temperature, Heart Rate, SpO2, and Blood Pressure with sub-second updates"
        },
        {
            icon: <Shield size={32} />,
            title: "Multi-Role Authentication",
            desc: "JWT-based secure access for Doctors, Patients, and Bystanders with role-based permissions"
        },
        {
            icon: <Wifi size={32} />,
            title: "IoT Data Ingestion",
            desc: "ESP32 sensors publish live data via HiveMQ Cloud MQTT broker to backend server"
        },
        {
            icon: <Smartphone size={32} />,
            title: "Cross-Platform Mobile",
            desc: "React Native (Expo) mobile app with QR code scanning for bystander emergency access"
        },
        {
            icon: <AlertTriangle size={32} />,
            title: "Intelligent Warnings",
            desc: "Color-coded alerts (Green/Yellow/Red) based on configurable danger thresholds"
        },
        {
            icon: <Clock size={32} />,
            title: "Historical Analytics",
            desc: "Interactive charts with 7 time ranges (10min to 24hr) for trend analysis"
        }
    ];

    const techStack = [
        { category: "IoT Hardware", items: ["ESP32", "Temperature Sensor", "Heart Rate Sensor", "SpO2 Sensor"] },
        { category: "Backend", items: ["Node.js", "Express.js", "Socket.io", "MQTT Client"] },
        { category: "Database", items: ["MongoDB Atlas", "Time-Series Data"] },
        { category: "Web Frontend", items: ["React.js", "Vite", "Chart.js", "Glassmorphism UI"] },
        { category: "Mobile App", items: ["React Native", "Expo", "expo-camera", "QR Scanning"] },
        { category: "Security", items: ["JWT Authentication", "Bcrypt", "Role-Based Access"] }
    ];

    const systemArchitecture = [
        {
            layer: "Edge Node",
            component: "ESP32 Sensors",
            description: "Generates live vital data and publishes JSON payloads to MQTT broker"
        },
        {
            layer: "Message Broker",
            component: "HiveMQ Cloud",
            description: "MQTT broker absorbing high-frequency data stream from IoT devices"
        },
        {
            layer: "Backend Server",
            component: "Node.js + Express",
            description: "Subscribes to MQTT, persists to MongoDB, broadcasts via Socket.io"
        },
        {
            layer: "Frontend Apps",
            component: "React + React Native",
            description: "Consumes REST APIs for history, WebSockets for live updates"
        }
    ];

    const userRoles = [
        {
            role: "Doctor Portal",
            desc: "Secure login to monitor multiple patients and view their live dashboards simultaneously",
            color: "#00f5ff"
        },
        {
            role: "Patient Portal",
            desc: "Secure access to personal health history and real-time metrics",
            color: "#b794f6"
        },
        {
            role: "Bystander Access",
            desc: "Scan patient's QR code to instantly access live vitals without an account",
            color: "#ff6b9d"
        }
    ];

    return (
        <div className="soul-project">
            {/* Header */}
            <header className="project-header">
                <button onClick={() => navigate('/#projects')} className="back-btn">
                    <ArrowLeft size={20} /> Back to Portfolio
                </button>

                <div className="project-hero">
                    <span className="project-tag">Freelance Project</span>
                    <h1>Health Care IoT Dashboard</h1>
                    <h2>Real-Time Health Monitoring System</h2>
                    <p className="tagline">
                        A comprehensive, real-time health monitoring platform designed for patients, doctors, and bystanders.
                        The system ingests live vital data from ESP32 IoT devices and distributes it securely across a modern
                        React Web Dashboard and React Native mobile application.
                    </p>
                </div>
            </header>

            {/* System Architecture */}
            <section className="section">
                <h3>System Architecture</h3>
                <div className="features-grid">
                    {systemArchitecture.map((item, index) => (
                        <div key={index} className="feature-card">
                            <div className="feature-icon">{item.component === "ESP32 Sensors" ? <Wifi size={32} /> : item.component === "HiveMQ Cloud" ? <Server size={32} /> : item.component === "Node.js + Express" ? <Database size={32} /> : <Users size={32} />}</div>
                            <h4>{item.layer}</h4>
                            <p style={{ fontWeight: '600', color: 'var(--primary)', marginBottom: '0.5rem' }}>{item.component}</p>
                            <p>{item.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Key Features */}
            <section className="section">
                <h3>Key Features</h3>
                <div className="features-grid">
                    {features.map((feature, index) => (
                        <div key={index} className="feature-card">
                            <div className="feature-icon">{feature.icon}</div>
                            <h4>{feature.title}</h4>
                            <p>{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Multi-Role Access */}
            <section className="section">
                <h3>Multi-Role Authentication & Access</h3>
                <div className="problem-solution">
                    {userRoles.map((user, index) => (
                        <div key={index} className="ps-card" style={{ borderLeft: `4px solid ${user.color}` }}>
                            <h4 style={{ color: user.color }}>{user.role}</h4>
                            <p>{user.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Technology Stack */}
            <section className="section">
                <h3>Technology Stack</h3>
                <div className="tech-grid">
                    {techStack.map((tech, index) => (
                        <div key={index} className="tech-category">
                            <h4>{tech.category}</h4>
                            <div className="tech-items">
                                {tech.items.map((item, i) => (
                                    <span key={i} className="tech-badge">{item}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Real-Time Monitoring */}
            <section className="section">
                <h3>Real-Time Monitoring & Graphing</h3>
                <div className="problem-solution">
                    <div className="ps-card solution">
                        <h4>Vital Metrics Tracked</h4>
                        <ul>
                            <li>🌡️ Temperature - Body temperature monitoring</li>
                            <li>❤️ Heart Rate - BPM with irregular heartbeat detection</li>
                            <li>💧 SpO2 - Blood oxygen saturation levels</li>
                            <li>🩺 Blood Pressure - Systolic/Diastolic readings</li>
                        </ul>
                    </div>
                    <div className="ps-card solution">
                        <h4>Live Updates & Visualization</h4>
                        <ul>
                            <li>Data flows from ESP32 → MQTT → MongoDB → Frontend</li>
                            <li>Socket.io for sub-second live updates</li>
                            <li>Interactive charts with 7 time ranges (10min to 24hr)</li>
                            <li>Offline resilience - graphs hold values during network drops</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Intelligent Warnings */}
            <section className="section">
                <h3>Intelligent Warnings System</h3>
                <div className="overview-content">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginTop: '1.5rem' }}>
                        <div style={{
                            background: 'rgba(0, 255, 136, 0.1)',
                            padding: '1.5rem',
                            borderRadius: '16px',
                            border: '1px solid rgba(0, 255, 136, 0.3)',
                            textAlign: 'center'
                        }}>
                            <h4 style={{ color: '#00ff88', marginBottom: '0.5rem' }}>🟢 Normal</h4>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>All vitals within healthy thresholds</p>
                        </div>
                        <div style={{
                            background: 'rgba(255, 193, 7, 0.1)',
                            padding: '1.5rem',
                            borderRadius: '16px',
                            border: '1px solid rgba(255, 193, 7, 0.3)',
                            textAlign: 'center'
                        }}>
                            <h4 style={{ color: '#ffc107', marginBottom: '0.5rem' }}>🟡 Warning</h4>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>Vitals approaching danger levels</p>
                        </div>
                        <div style={{
                            background: 'rgba(255, 59, 48, 0.1)',
                            padding: '1.5rem',
                            borderRadius: '16px',
                            border: '1px solid rgba(255, 59, 48, 0.3)',
                            textAlign: 'center'
                        }}>
                            <h4 style={{ color: '#ff3b30', marginBottom: '0.5rem' }}>🔴 Danger</h4>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>Critical thresholds exceeded - immediate action required</p>
                        </div>
                    </div>
                    <p style={{ marginTop: '2rem', color: 'var(--text-secondary)' }}>
                        Custom danger thresholds can be configured dynamically for each health metric based on patient needs.
                    </p>
                </div>
            </section>

            {/* Mobile App Features */}
            <section className="section">
                <h3>Mobile Application Features</h3>
                <div className="problem-solution">
                    <div className="ps-card solution">
                        <h4><QrCode size={20} style={{ marginRight: '8px' }} />QR Code Scanning</h4>
                        <ul>
                            <li>Bystanders can scan patient's QR code</li>
                            <li>Instant access to live vitals without account</li>
                            <li>Expo Camera integration for native scanning</li>
                            <li>Offline capable with cached data</li>
                        </ul>
                    </div>
                    <div className="ps-card solution">
                        <h4><Smartphone size={20} style={{ marginRight: '8px' }} />Cross-Platform Support</h4>
                        <ul>
                            <li>Built with React Native (Expo)</li>
                            <li>Works on both iOS and Android</li>
                            <li>Hardware native integrations</li>
                            <li>Dark theme optimized (#0f0f1e)</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Data Flow */}
            <section className="section">
                <h3>Data Flow & Communication</h3>
                <div className="workflow">
                    <div className="workflow-step">
                        <div className="step-number">1</div>
                        <h4>ESP32 Publishes Data</h4>
                        <p>IoT sensors generate JSON payload with vital readings</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">2</div>
                        <h4>MQTT Broker</h4>
                        <p>HiveMQ Cloud receives and queues the data stream</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">3</div>
                        <h4>Backend Processing</h4>
                        <p>Node.js subscribes, validates, saves to MongoDB</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">4</div>
                        <h4>Real-Time Broadcast</h4>
                        <p>Socket.io emits to specific user rooms instantly</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">5</div>
                        <h4>Client Update</h4>
                        <p>React/React Native apps receive and display live data</p>
                    </div>
                </div>
            </section>

            {/* Security Implementation */}
            <section className="section">
                <h3>Security Implementation</h3>
                <div className="overview-content">
                    <h4 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Multi-Layer Security</h4>
                    <ul style={{ color: 'var(--text-secondary)', lineHeight: '2', marginLeft: '2rem' }}>
                        <li><strong>JWT Authentication:</strong> Secure token-based session management</li>
                        <li><strong>Bcrypt Password Hashing:</strong> Industry-standard password encryption</li>
                        <li><strong>Role-Based Access Control:</strong> Strict permissions for Doctor/Patient/Bystander</li>
                        <li><strong>Encrypted MQTT:</strong> Secure communication between ESP32 and broker</li>
                        <li><strong>User Isolation:</strong> Patients can only access their own data</li>
                        <li><strong>Doctor Monitoring:</strong> Doctors can only view assigned patients</li>
                    </ul>
                </div>
            </section>

            {/* Project Links */}
            <section className="section">
                <div className="project-links">
                    <a href="https://github.com/ANJOEJAMES" target="_blank" rel="noreferrer" className="project-btn">
                        <Github size={20} /> View on GitHub
                    </a>
                    <button onClick={() => navigate('/#projects')} className="project-btn secondary">
                        <ArrowLeft size={20} /> Back to Portfolio
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="project-footer">
                <p>© {new Date().getFullYear()} Anjoe James. Health Care IoT Dashboard Project.</p>
            </footer>
        </div>
    );
}

export default HealthIotProject;
